/**
 * ExecutionsTab
 * Redesigned executions list with status stat chips, filter controls,
 * pagination, and a side drawer (ExecutionDetailDrawer) for full details
 * rather than a cramped modal dialog.
 */

import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  History as HistoryIcon,
  Visibility as ViewIcon,
  FilterAltOff as ClearIcon,
} from '@mui/icons-material';

import { agentApi } from '../../../services/api';
import { SectionCard } from './primitives';
import { ExecutionDetailDrawer } from './ExecutionDetailDrawer';
import {
  formatModelPricing,
  getModelDisplayName,
} from '../../../utils/modelPricing';

interface ExecutionsTabProps {
  agentId: number;
}

type StatusFilter = 'all' | 'COMPLETED' | 'ERROR' | 'RUNNING' | 'CANCELLED';

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  COMPLETED: 'success',
  ERROR: 'error',
  RUNNING: 'warning',
  CANCELLED: 'default',
};

export const ExecutionsTab: React.FC<ExecutionsTabProps> = ({ agentId }) => {
  const theme = useTheme();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['agent-executions', agentId],
    queryFn: () => agentApi.getExecutions(agentId),
    enabled: !!agentId,
  });

  const rows = data?.results || [];

  const stats = useMemo(() => {
    const completed = rows.filter((r) => r.status === 'COMPLETED');
    const errored = rows.filter((r) => r.status === 'ERROR');
    const running = rows.filter((r) => r.status === 'RUNNING');
    const cancelled = rows.filter((r) => r.status === 'CANCELLED');
    const totalCost = rows.reduce((s, r) => s + (r.total_cost || 0), 0);
    const totalTokens = rows.reduce((s, r) => s + (r.total_tokens || 0), 0);
    const avgDuration =
      rows.length > 0
        ? rows.reduce((s, r) => s + (r.duration_seconds || 0), 0) / rows.length
        : 0;
    const successRate = rows.length > 0 ? completed.length / rows.length : 0;
    return {
      total: rows.length,
      completed: completed.length,
      errored: errored.length,
      running: running.length,
      cancelled: cancelled.length,
      totalCost,
      totalTokens,
      avgDuration,
      successRate,
    };
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (needle) {
        const hay = `${r.id} ${r.system_name || ''} ${r.status} ${r.execution_type || ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, statusFilter, search]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('all');
  };

  if (error) {
    return <Alert severity="error">Failed to load executions.</Alert>;
  }

  return (
    <Stack spacing={{ xs: 2, sm: 2.5 }}>
      <SectionCard
        icon={<HistoryIcon fontSize="small" />}
        title="Executions"
        description={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
            <Chip size="small" variant="outlined" label={`${stats.total} total`} />
            <Chip size="small" color="success" variant="outlined" label={`${stats.completed} completed`} />
            {stats.errored > 0 && (
              <Chip size="small" color="error" variant="outlined" label={`${stats.errored} errored`} />
            )}
            {stats.running > 0 && (
              <Chip size="small" color="warning" variant="outlined" label={`${stats.running} running`} />
            )}
            {stats.cancelled > 0 && (
              <Chip size="small" variant="outlined" label={`${stats.cancelled} cancelled`} />
            )}
            {stats.total > 0 && (
              <Chip
                size="small"
                variant="outlined"
                label={`${Math.round(stats.successRate * 100)}% success`}
              />
            )}
            {stats.total > 0 && (
              <Chip
                size="small"
                variant="outlined"
                label={`avg ${stats.avgDuration.toFixed(1)}s`}
              />
            )}
            {stats.totalCost > 0 && (
              <Chip
                size="small"
                variant="outlined"
                label={`$${stats.totalCost.toFixed(4)} spent`}
              />
            )}
            {stats.totalTokens > 0 && (
              <Chip
                size="small"
                variant="outlined"
                label={`${stats.totalTokens.toLocaleString()} tokens`}
              />
            )}
          </Stack>
        }
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.25}
          sx={{ mb: 1.5 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder="Search by id or system"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
            {(['all', 'COMPLETED', 'ERROR', 'RUNNING', 'CANCELLED'] as StatusFilter[]).map((s) => (
              <Chip
                key={s}
                label={s === 'all' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
                size="small"
                color={
                  statusFilter === s
                    ? s === 'all'
                      ? 'primary'
                      : STATUS_COLORS[s]
                    : 'default'
                }
                variant={statusFilter === s ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter(s)}
              />
            ))}
            <Tooltip title="Reset filters">
              <span>
                <IconButton
                  size="small"
                  onClick={resetFilters}
                  disabled={search === '' && statusFilter === 'all'}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {isLoading ? (
          <Stack spacing={1}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={40} />
            ))}
          </Stack>
        ) : filtered.length === 0 ? (
          <Alert severity="info">
            {rows.length === 0
              ? 'No executions yet. Start a chat with this agent to see its history here.'
              : 'No executions match the current filters.'}
          </Alert>
        ) : (
          <Box
            sx={{
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                    <TableCell>Status</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>System</TableCell>
                    <TableCell>Model Snapshot</TableCell>
                    <TableCell align="right">Duration</TableCell>
                    <TableCell align="right">Tokens</TableCell>
                    <TableCell align="right">Cost</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((execution) => (
                    <TableRow
                      key={execution.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setSelectedId(execution.id)}
                    >
                      <TableCell>
                        <Chip
                          label={execution.status}
                          size="small"
                          color={STATUS_COLORS[execution.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={(execution.execution_type || 'agent').replace(/_/g, ' ')}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        {execution.id.substring(0, 8)}…
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                          {execution.system_name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={formatModelPricing(execution)} arrow>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                            {getModelDisplayName(execution, 'No model recorded')}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">{execution.duration_seconds.toFixed(2)}s</TableCell>
                      <TableCell align="right">{execution.total_tokens.toLocaleString()}</TableCell>
                      <TableCell align="right">${execution.total_cost.toFixed(4)}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(execution.started_at).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View full details">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedId(execution.id);
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Box>
        )}
      </SectionCard>

      <ExecutionDetailDrawer
        executionId={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
      />
    </Stack>
  );
};

export default ExecutionsTab;
